<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {QMenu, QSelect} from "quasar";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import EditScreenField from "@/components/EditView/editScreens/EditScreenField.vue";
import Translator from "@/utils/common/Translator.ts";
import TechnicianAppointmentUtils from "@/utils/common/TechnicianAppointmentUtils.ts";
import MyAppointmentsUtils, {AppointmentFilters, FilterRow, TECHNICIAN_FILTER_KEY} from "@/utils/common/MyAppointmentsUtils.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import CriteriaBuilder from "@/utils/criteria/CriteriaBuilder.ts";

type FieldMetaData = namaTypes.FieldMetaData;
type EditScreenFieldDefinition = namaTypes.EditScreenField & { namaClassTypeId: string };

interface FilterField {
  key: string;
  labelKey: string;
  icon: string;
  metaData: FieldMetaData;
  crewOnly?: boolean;
  pickerCriteria?: () => namaTypes.DTOCriteria | undefined;
}

const props = withDefaults(defineProps<{
  technicianIds?: string[];
  sectionIds?: string[];
  crews?: namaTypes.DTOTechnicianCrewInfo[];
  showCrewFilters?: boolean;
  excludeKeys?: string[]
}>(), {
  technicianIds: () => [],
  sectionIds: () => [],
  crews: () => [],
  excludeKeys: () => []
});
const emit = defineEmits<{ change: [filters: AppointmentFilters] }>();

const translate = Translator.translate;
const statusStyles = TechnicianAppointmentUtils.STATUS_STYLES;
const rowFieldId = MyAppointmentsUtils.rowFieldId;

function editField(fieldId: string): EditScreenFieldDefinition {
  return {namaClassTypeId: "EditScreenField", id: fieldId, hideTitle: true};
}

function withFieldId(fieldId: string, metaData: FieldMetaData): FieldMetaData {
  return {detail: false, required: false, enabled: true, ...metaData, fieldId, id: fieldId};
}

function referenceMeta(referencedEntityType: string): FieldMetaData {
  return {fieldType: FieldType.Reference, referencedEntityType};
}

function idsCriteria(fieldPath: string, ids: string[]): namaTypes.DTOCriteria | undefined {
  return ids.length ? CriteriaBuilder.create().field(fieldPath).in(...ids).build() : undefined;
}

const FILTER_FIELDS: FilterField[] = [
  {
    key: TECHNICIAN_FILTER_KEY, labelKey: "technician", icon: "engineering", metaData: referenceMeta("Employee"), crewOnly: true,
    pickerCriteria: () => idsCriteria("id", props.technicianIds)
  },
  {
    key: TechnicianAppointmentUtils.DEPARTMENT_FIELD_ID, labelKey: "departmentSection", icon: "account_tree",
    metaData: referenceMeta(TechnicianAppointmentUtils.DEPARTMENT_ENTITY), crewOnly: true,
    pickerCriteria: () => idsCriteria("id", props.sectionIds)
  },
  {key: "customer", labelKey: "customer", icon: "person", metaData: referenceMeta("Customer")},
  {key: "branch", labelKey: "branch", icon: "store", metaData: referenceMeta("Branch")},
  {key: "region", labelKey: "area", icon: "place", metaData: referenceMeta("AddressRegion")},
  {key: "technicianProcedure", labelKey: "technicianProcedure", icon: "handyman", metaData: referenceMeta(TechnicianAppointmentUtils.PROCEDURE_ENTITY)},
  {
    key: "fromDoc", labelKey: "fromDoc", icon: "description",
    metaData: {fieldType: FieldType.Genericreference, allowedValues: [CommonConstants.GENERIC_REF_ALL_DOCS]}
  },
  {
    key: "technicianAppointment", labelKey: "technicianAppointment", icon: "event_note",
    metaData: referenceMeta(TechnicianAppointmentUtils.APPOINTMENT_ENTITY),
    pickerCriteria: () => idsCriteria("details.technicianCrew.id", MyAppointmentsUtils.crewIds(props.crews))
  }
];
const FIELD_BY_KEY = new Map(FILTER_FIELDS.map(field => [field.key, field]));
const availableFields = computed(() => FILTER_FIELDS.filter(field =>
  (props.showCrewFilters || !field.crewOnly) && !props.excludeKeys.includes(field.key)));

const FROM_DATE_FIELD = editField("fromDate");
const FROM_DATE_META = withFieldId("fromDate", {fieldType: FieldType.Date});
const TO_DATE_FIELD = editField("toDate");
const TO_DATE_META = withFieldId("toDate", {fieldType: FieldType.Date});

const MATCH_OPTIONS = [{value: "all", label: translate("all")}, {value: "any", label: translate("myAppointmentsMatchAny")}];
const OPERATOR_OPTIONS = [{value: "Equal", label: translate("Equal")}, {value: "NotEqual", label: translate("NotEqual")}];

const filterPresenter = new QuestionFieldsPresenter(TechnicianAppointmentUtils.APPOINTMENT_ENTITY);
const builderOpen = ref(false);
const matchMode = ref("all");
const rows = ref<FilterRow[]>([]);
const selectedStatuses = ref<Set<string>>(new Set());
let nextRowUid = 1;

const activeRuleCount = computed(() => rows.value.filter(row => hasFilterValue(rowFieldId(row))).length);
const periodActive = computed(() => hasFilterValue("fromDate") || hasFilterValue("toDate"));
const anythingActive = computed(() => activeRuleCount.value > 0 || periodActive.value || selectedStatuses.value.size > 0 || rows.value.length > 0);

function hasFilterValue(fieldId: string): boolean {
  return MyAppointmentsUtils.hasFilterValue((filterPresenter.record.value as any)?.[fieldId]);
}

function fieldOf(row: FilterRow): FilterField {
  return FIELD_BY_KEY.get(row.key)!;
}

function rowMetaData(row: FilterRow): FieldMetaData {
  const fieldId = rowFieldId(row);
  const metaData = fieldOf(row).metaData;
  return withFieldId(fieldId, metaData.fieldType === FieldType.Reference
    ? {...metaData, fieldInformation: {fieldId, hasSearchContext: false}}
    : metaData);
}

function fieldLabel(field: FilterField): string {
  return translate(field.labelKey);
}

// Quasar requires @filter whenever use-input is set; these dropdowns never search.
function noOpFilter(_value: string, update: (callback: () => void) => void) {
  update(() => {});
}

function clearRowValue(row: FilterRow) {
  const {[rowFieldId(row)]: _removed, ...rest} = filterPresenter.record.value as any;
  filterPresenter.record.value = rest;
}

function addRow() {
  const unused = availableFields.value.find(field => !rows.value.some(row => row.key === field.key));
  rows.value.push({uid: nextRowUid++, key: (unused ?? availableFields.value[0]).key, negate: false});
}

function changeRowField(row: FilterRow, key: string) {
  clearRowValue(row);
  row.key = key;
  row.uid = nextRowUid++;
}

function removeRow(row: FilterRow) {
  clearRowValue(row);
  rows.value = rows.value.filter(other => other.uid !== row.uid);
}

function isStatusSelected(status: string): boolean {
  return selectedStatuses.value.has(status);
}

function toggleStatus(status: string) {
  const next = new Set(selectedStatuses.value);
  if (next.has(status))
    next.delete(status);
  else
    next.add(status);
  selectedStatuses.value = next;
}

function pickerCriteria(fieldId?: string): namaTypes.DTOCriteria | undefined {
  const row = rows.value.find(candidate => rowFieldId(candidate) === fieldId);
  return row ? fieldOf(row).pickerCriteria?.() : undefined;
}

filterPresenter.fetchCriteriaFromFilterField = (_referencedEntityType?: string, fieldId?: string) => pickerCriteria(fieldId);
filterPresenter.getDefaultCriteria = (fieldId: string) => pickerCriteria(fieldId);

function clearFilters() {
  filterPresenter.record.value = {};
  selectedStatuses.value = new Set();
  rows.value = [];
  matchMode.value = "all";
}

watch(() => MyAppointmentsUtils.readFilters(filterPresenter.record.value, [...selectedStatuses.value], rows.value,
    matchMode.value === "any", props.crews),
  filters => emit("change", filters));
</script>

<template>
  <section class="appt-card my-appt__filters">
    <div class="my-appt__filters-head">
      <button type="button" class="my-appt__filters-toggle" :class="{ 'my-appt__filters-toggle--open': builderOpen }"
              :aria-expanded="builderOpen">
        <NamaIcon default-icon="tune" size="18px" class="my-appt__icon"/>
        <span>{{ translate('myAppointmentsFilters') }}</span>
        <span v-if="activeRuleCount" class="my-appt__filters-count">{{ activeRuleCount }}</span>
        <NamaIcon default-icon="expand_more" size="18px" class="my-appt__icon my-appt__filters-chevron"/>

        <q-menu v-model="builderOpen" anchor="bottom start" self="top start" :offset="[0, 8]"
                class="my-appt__builder appt-theme">
          <div class="my-appt__builder-body">
            <div class="my-appt__match">
              <span>{{ translate('myAppointmentsMatch') }}</span>
              <q-select v-model="matchMode" :options="MATCH_OPTIONS" emit-value map-options dense outlined options-dense
                        hide-bottom-space use-input hide-selected fill-input input-debounce="0" @filter="noOpFilter"
                        class="appt-field my-appt__select my-appt__select--match nama-customize-dense-input nama-field-input"
                        input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
                        options-selected-class="tw-text-base tw-font-normal tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
                        popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"/>
              <span>{{ translate('myAppointmentsOfTheFollowingFilters') }}</span>
            </div>

            <div class="my-appt__rules">
              <p v-if="!rows.length" class="my-appt__rules-empty">{{ translate('myAppointmentsNoFilters') }}</p>
              <div
                v-for="row in rows"
                :key="row.uid"
                class="my-appt__rule"
                :class="{ 'my-appt__rule--active': hasFilterValue(rowFieldId(row)) }">
                <q-select
                  :model-value="row.key"
                  :options="availableFields"
                  option-value="key"
                  :option-label="fieldLabel"
                  emit-value map-options dense outlined options-dense
                  hide-bottom-space use-input hide-selected fill-input input-debounce="0"
                  class="appt-field my-appt__select nama-customize-dense-input nama-field-input"
                  input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
                  options-selected-class="tw-text-base tw-font-normal tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
                  popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
                  @filter="noOpFilter"
                  @update:model-value="key => changeRowField(row, key)">
                  <template #prepend>
                    <NamaIcon :default-icon="fieldOf(row).icon" size="16px" class="my-appt__icon my-appt__rule-icon"/>
                  </template>
                </q-select>
                <q-select
                  :model-value="row.negate ? 'NotEqual' : 'Equal'"
                  :options="OPERATOR_OPTIONS"
                  emit-value map-options dense outlined options-dense
                  hide-bottom-space use-input hide-selected fill-input input-debounce="0"
                  class="appt-field my-appt__select my-appt__select--operator nama-customize-dense-input nama-field-input"
                  input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
                  options-selected-class="tw-text-base tw-font-normal tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white"
                  popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
                  @filter="noOpFilter"
                  @update:model-value="value => row.negate = value === 'NotEqual'"/>
                <EditScreenField class="appt-field my-appt__rule-value" :do-not-apply-theme-layouts="true" :presenter="filterPresenter"
                                 :field="editField(rowFieldId(row))" :field-meta-data="rowMetaData(row)"/>
                <button type="button" class="my-appt__rule-remove" :aria-label="translate('remove')" @click="removeRow(row)">
                  <NamaIcon default-icon="remove" size="16px" class="my-appt__icon"/>
                </button>
              </div>
            </div>

            <button type="button" class="my-appt__rule-add" @click="addRow">
              <NamaIcon default-icon="add" size="18px" class="my-appt__icon"/>
              <span>{{ translate('myAppointmentsAddFilter') }}</span>
            </button>
          </div>

          <div class="my-appt__builder-foot">
            <button type="button" class="my-appt__builder-reset" :disabled="!anythingActive" @click="clearFilters">
              <NamaIcon default-icon="restart_alt" size="16px" class="my-appt__icon"/>
              <span>{{ translate('clearFilters') }}</span>
            </button>
          </div>
        </q-menu>
      </button>

      <div class="my-appt__statuses" role="group" :aria-label="translate('status')">
        <button
          v-for="style in statusStyles"
          :key="style.status"
          type="button"
          class="my-appt__status"
          :class="{ 'my-appt__status--on': isStatusSelected(style.status) }"
          :style="{ '--status-color': style.color }"
          :aria-pressed="isStatusSelected(style.status)"
          @click="toggleStatus(style.status)">
          <span class="my-appt__status-dot"></span>
          <span>{{ translate(style.status) }}</span>
        </button>
      </div>

      <div class="my-appt__period" :class="{ 'my-appt__period--active': periodActive }">
        <NamaIcon default-icon="date_range" size="16px" class="my-appt__icon"/>
        <EditScreenField class="appt-field" :do-not-apply-theme-layouts="true" :presenter="filterPresenter"
                         :field="FROM_DATE_FIELD" :field-meta-data="FROM_DATE_META"/>
        <span class="my-appt__period-sep">–</span>
        <EditScreenField class="appt-field" :do-not-apply-theme-layouts="true" :presenter="filterPresenter"
                         :field="TO_DATE_FIELD" :field-meta-data="TO_DATE_META"/>
      </div>

      <button v-if="anythingActive" type="button" class="my-appt__filters-clear" @click="clearFilters">
        <NamaIcon default-icon="close" size="16px" class="my-appt__icon"/>
        <span>{{ translate('clearFilters') }}</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.my-appt__filters {
  padding: 10px 14px;
}

.my-appt__icon {
  /* Beats the global `i.nama-icon` gray rule so icons follow their button's colour. */
  color: inherit;
}

.my-appt__icon:not(.has-fill) :deep(path) {
  fill: currentColor;
}

.my-appt__filters-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 14px;
}

.my-appt__filters-toggle,
.my-appt__status,
.my-appt__filters-clear,
.my-appt__rule-remove,
.my-appt__rule-add,
.my-appt__builder-reset {
  font: inherit;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.my-appt__filters-toggle:focus-visible,
.my-appt__status:focus-visible,
.my-appt__filters-clear:focus-visible,
.my-appt__rule-remove:focus-visible,
.my-appt__rule-add:focus-visible,
.my-appt__builder-reset:focus-visible {
  outline: 2px solid var(--appt-accent);
  outline-offset: 2px;
}

.my-appt__filters-toggle {
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--appt-border);
  border-radius: 999px;
  background: var(--appt-surface);
  color: var(--appt-text-strong);
  font-size: 13px;
  font-weight: 700;
}

.my-appt__filters-toggle:hover,
.my-appt__filters-toggle--open {
  background: var(--appt-chip-bg);
}

.my-appt__filters-count {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--appt-accent);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.my-appt__filters-chevron {
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.my-appt__filters-toggle--open .my-appt__filters-chevron {
  transform: rotate(180deg);
}

.my-appt__statuses {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.my-appt__status {
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid var(--appt-border);
  background: var(--appt-surface);
  color: var(--appt-text-body);
  font-size: 12px;
  font-weight: 600;
}

.my-appt__status:hover {
  background: var(--appt-chip-bg);
}

.my-appt__status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--status-color, #64748b);
  flex: 0 0 auto;
}

.my-appt__status--on,
.my-appt__status--on:hover {
  border-color: var(--status-color, #64748b);
  background: color-mix(in srgb, var(--status-color, #64748b) 14%, var(--appt-surface));
  color: var(--appt-text-strong);
}

.my-appt__period {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--appt-text-muted);
}

.my-appt__period > .appt-field {
  width: 9.5rem;
}

.my-appt__period--active {
  color: var(--appt-accent);
}

.my-appt__period-sep {
  color: var(--appt-text-fainter);
}

.my-appt__filters-clear {
  gap: 5px;
  margin-inline-start: auto;
  padding: 5px 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--appt-text-muted);
  font-size: 12.5px;
  font-weight: 600;
}

.my-appt__filters-clear:hover {
  background: var(--appt-danger-soft);
  color: var(--appt-danger);
}


.my-appt__match {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--appt-text-strong);
}

/* the theme's soft fill keys off `.appt-field .q-field--outlined`; these selects ARE the q-field, so repeat it here */
.my-appt__select :deep(.q-field__control):before {
  background: var(--appt-chip-bg);
  border-color: transparent;
}

.my-appt__select:hover :deep(.q-field__control):before {
  background: var(--appt-chip-hover);
  border-color: var(--appt-border);
}

.my-appt__select.q-field--focused :deep(.q-field__control):before {
  background: var(--appt-surface);
  border-color: var(--appt-accent);
  box-shadow: 0 0 0 3px var(--appt-accent-soft);
}

.my-appt__select--match {
  width: 7rem;
}

.my-appt__select--operator {
  width: 6.5rem;
}

.my-appt__rules {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 50vh;
  overflow-y: auto;
}

.my-appt__rules-empty {
  margin: 0;
  padding: 10px 4px;
  font-size: 12.5px;
  color: var(--appt-text-muted);
}

.my-appt__rule {
  display: grid;
  grid-template-columns: 11rem 6.5rem minmax(0, 1fr) 28px;
  align-items: center;
  gap: 8px;
  padding: 3px 4px;
  border-radius: 10px;
}

.my-appt__rule:hover {
  background: var(--appt-border-soft);
}

.my-appt__rule-value {
  min-width: 0;
}

/* the generic-reference widget splits into type + document; its 1rem gap is too wide for a filter row */
.my-appt__rule-value :deep(.tw-gap-4) {
  gap: 6px;
}

.my-appt__rule-icon {
  color: var(--appt-text-muted);
}

.my-appt__rule--active .my-appt__rule-icon {
  color: var(--appt-accent);
}

.my-appt__rule-remove {
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--appt-text-fainter);
}

.my-appt__rule-remove:hover {
  background: var(--appt-danger-soft);
  color: var(--appt-danger);
}

.my-appt__rule-add {
  gap: 6px;
  margin-top: 10px;
  padding: 5px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--appt-text-muted);
  font-size: 12.5px;
  font-weight: 600;
}

.my-appt__rule-add:hover {
  background: var(--appt-chip-hover);
  color: var(--appt-text-strong);
}

.my-appt__builder-foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--appt-border-soft);
}

.my-appt__builder-reset {
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--appt-border);
  border-radius: 999px;
  background: var(--appt-surface);
  color: var(--appt-text-muted);
  font-size: 12.5px;
  font-weight: 600;
}

.my-appt__builder-reset:hover:not(:disabled) {
  background: var(--appt-danger-soft);
  border-color: var(--appt-danger-soft-border);
  color: var(--appt-danger);
}

.my-appt__builder-reset:disabled {
  cursor: default;
  opacity: 0.5;
}

@media (max-width: 640px) {
  .my-appt__rule {
    grid-template-columns: minmax(0, 1fr) 6.5rem auto;
  }

  .my-appt__rule-value {
    grid-column: 1 / -1;
    grid-row: 2;
  }
}
</style>

<!-- QMenu teleports to body and its root element carries no scoped data-v attribute, so the popover
     surface itself has to be styled globally; everything inside it is scoped as usual. -->
<style>
.my-appt__builder.q-menu {
  width: min(48rem, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  padding: 14px;
  border: 1px solid var(--appt-border);
  border-radius: 14px;
  background: var(--appt-surface);
  color: var(--appt-text);
  box-shadow: var(--appt-card-shadow);
}
</style>
